// ============================================================================
// News on the things a person follows (item #650, TCSD-0189 rulings 6–8).
//
// The grouping of deliveries into the six kinds lives in the database
// (news_kind()); this file only names the six for the screen, in the order the
// settings page shows them, and reads / writes the person's own settings row.
// The settings are kept inside the existing channel_matrix (Ben, 2026-09-28,
// D6) under `news`: { cadence: 'daily' | 'weekly' | 'off', off: [kinds] }.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { commitWrite, useReader } from '../../shared/save-path';

export const NEWS_KINDS = [
  ['project_cards_closed', 'Project cards I follow getting closed'],
  ['sub_and_rate_changes', 'Sub and rate changes at my facilities'],
  ['complaints_resolved', 'Complaints resolved'],
  ['starts_and_stops', 'Starts and stops in my territory'],
  ['colleagues_cards', "Colleagues' cards in my territory"],
  ['fleet', 'Fleet and vehicle reminders'],
] as const;
export type NewsKind = (typeof NEWS_KINDS)[number][0];
export const NEWS_LABEL = Object.fromEntries(NEWS_KINDS) as Record<NewsKind, string>;

export type NewsCadence = 'daily' | 'weekly' | 'off';

export interface NewsItem {
  recipient_row_id: string;
  notification_id: string;
  event_type: string;
  entity_type: string;
  entity_id: string;
  title: string;
  created_at: string;
  read_at: string | null;
  news_kind: NewsKind;
}

type Matrix = Record<string, unknown> & {
  chat_dm?: { enabled?: boolean; muted_reasons?: string[] };
  email?: { enabled?: boolean; digest?: boolean; immediate_reasons?: string[] };
  news?: { cadence?: NewsCadence; off?: string[] };
};

export function useNewsPrefs() {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useReader(['user_notification_prefs'], reload);

  useEffect(() => {
    if (!eid) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase
        .from('user_notification_prefs')
        .select('channel_matrix')
        .eq('employee_id', eid)
        .maybeSingle();
      if (cancelled) return;
      if (e) setError(errorMessage(e, 'Could not load your notification settings.'));
      setMatrix(((data as { channel_matrix?: Matrix } | null)?.channel_matrix ?? {}) as Matrix);
    })();
    return () => {
      cancelled = true;
    };
  }, [eid, tick]);

  /** One statement, proven to land (TCSD-0168), then every reader of the table refreshes. */
  const save = useCallback(
    async (next: Matrix) => {
      if (!eid) return;
      await commitWrite({
        label: 'save your notification settings',
        write: supabase
          .from('user_notification_prefs')
          .upsert({ employee_id: eid, channel_matrix: next as never }, { onConflict: 'employee_id' })
          .select('employee_id'),
        touches: ['user_notification_prefs'],
      });
      setMatrix(next);
    },
    [eid],
  );

  const cadence: NewsCadence = (matrix?.news?.cadence as NewsCadence) ?? 'daily';
  const off = new Set(matrix?.news?.off ?? []);
  return { matrix, cadence, off, save, error, loaded: matrix !== null };
}

/** My news for the window the cadence asks for: yesterday, or the last seven days. */
export function useMyNews(cadence: NewsCadence, enabled = true) {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useReader(['notification_recipients', 'user_notification_prefs'], reload);

  useEffect(() => {
    if (!eid || !enabled) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('get_my_news', { p_days: cadence === 'weekly' ? 7 : 1 });
      if (cancelled) return;
      setItems((data ?? []) as NewsItem[]);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [eid, cadence, enabled, tick]);

  return { items, loaded, reload };
}

export function newsSummary(items: NewsItem[]): string {
  const c = new Map<NewsKind, number>();
  for (const i of items) c.set(i.news_kind, (c.get(i.news_kind) ?? 0) + 1);
  return NEWS_KINDS.filter(([k]) => c.has(k))
    .map(([k, l]) => `${c.get(k)} · ${l.toLowerCase()}`)
    .join(' — ');
}
